import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useRole } from "../../components/AppShell";
import { BackIcon, PlusIcon, ProductsIcon, SearchIcon } from "../../components/icons";
import { Empty, ListSkeleton, useConfirm, useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import {
  useDeleteProduct,
  useImportFile,
  useImportStore,
  useLumiConfig,
  useProduct,
  useProductCategories,
  useProducts,
  useSaveProduct,
} from "../../lib/queries";
import type { Product, ProductDetail } from "../../lib/types";
import { missingFields } from "./completeness";
import { CSV_TEMPLATE, type CsvResult, readProductsCsv } from "./csv";
import { ImproveDescription } from "./ImproveDescription";
import { PhotoField, thumbUrl } from "./PhotoField";

const useCanEdit = () => {
  const role = useRole();
  return role === "owner" || role === "admin";
};

// ---- List ------------------------------------------------------------------

export function ProductsPage() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [kind, setKind] = useState<Product["kind"] | "">("");
  const [withInactive, setWithInactive] = useState(false);
  const list = useProducts(q, category, { kind, withInactive });
  const categories = useProductCategories();
  const canEdit = useCanEdit();
  const [importing, setImporting] = useState(false);
  const items = list.data?.items ?? [];
  const filtered = !!(q || category || kind);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Catálogo</h1>
        <label className="search-field">
          <SearchIcon />
          <input
            className="input"
            type="search"
            placeholder="Buscar por nombre, modelo o categoría"
            aria-label="Buscar en el catálogo"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <select
          className="select products-category"
          aria-label="Categoría"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">Todas las categorías</option>
          {categories.data?.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          className="select products-category"
          aria-label="Tipo"
          value={kind}
          onChange={(e) => setKind(e.target.value as Product["kind"] | "")}
        >
          <option value="">Todos los tipos</option>
          <option value="product">Solo productos</option>
          <option value="service">Solo servicios</option>
          <option value="part">Solo repuestos</option>
        </select>
        <label className="check products-inactive">
          <input type="checkbox" checked={withInactive} onChange={(e) => setWithInactive(e.target.checked)} />
          Mostrar inactivos
        </label>
        {canEdit && (
          <div className="products-actions">
            <button className="btn" type="button" onClick={() => setImporting(true)}>
              Importar
            </button>
            <Link className="btn btn-primary" to="/catalogo/$productId" params={{ productId: "nuevo" }}>
              <PlusIcon />
              Nuevo
            </Link>
          </div>
        )}
      </div>

      <div className="card">
        {list.isPending ? (
          <ListSkeleton />
        ) : items.length === 0 ? (
          <Empty title={filtered ? "Nada coincide." : "Todavía no hay productos ni servicios."}>
            <span>
              {filtered
                ? "Prueba con otra palabra, categoría o tipo."
                : "Agrégalos uno por uno o importa los productos desde la tienda en línea de la empresa."}
            </span>
          </Empty>
        ) : (
          <ul className="product-list">
            {items.map((p) => {
              const missing = missingFields(p);
              return (
                <li key={p.id}>
                  <Link to="/catalogo/$productId" params={{ productId: p.id }} className="product-row">
                    {p.images[0] ? (
                      <img src={thumbUrl(p.images[0])} alt="" loading="lazy" className="product-thumb" />
                    ) : (
                      <span className="product-thumb product-thumb-empty" aria-hidden="true">
                        <ProductsIcon />
                      </span>
                    )}
                    <span className="product-main">
                      <span className="product-name" title={p.name}>
                        {p.name}
                      </span>
                      <span className="product-meta">
                        {p.model ? (
                          <span className="mono">{p.model}</span>
                        ) : (
                          p.kind !== "service" && <span className="faint">Sin modelo</span>
                        )}
                        {p.category && <span>{p.category}</span>}
                      </span>
                    </span>
                    <span className="product-flags">
                      {p.kind === "service" && <span className="pill pill-human">Servicio</span>}
                      {p.kind === "part" && <span className="pill pill-bot">Repuesto</span>}
                      {p.status === "inactive" && <span className="pill pill-closed">Inactivo</span>}
                      {canEdit && missing.length > 0 && (
                        <span className="pill pill-warn" title={`Falta: ${missing.join(", ")}`}>
                          Por completar
                        </span>
                      )}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {importing && <ImportDialog onClose={() => setImporting(false)} />}
    </div>
  );
}

const IMPORT_ERRORS: Record<string, string> = {
  store_blocked:
    "Esa tienda no permite que otros sistemas lean su catálogo automáticamente. Exporta los productos a un archivo CSV e impórtalo aquí.",
  not_woocommerce: "Esa dirección no responde como una tienda WooCommerce. Revisa la dirección o usa un archivo CSV.",
  store_unavailable: "La tienda no respondió. Intenta de nuevo en unos minutos o usa un archivo CSV.",
  bad_url: "Escribe la dirección pública de la tienda, por ejemplo tienda.com.",
};

function ImportDialog({ onClose }: { onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const fromStore = useImportStore();
  const fromFile = useImportFile();
  const toast = useToast();
  const [mode, setMode] = useState<"file" | "store">("file");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<CsvResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  function done(r: { created: number; updated: number }) {
    toast(`${r.created} productos nuevos, ${r.updated} actualizados`);
    dialog.current?.close();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (mode === "store") done(await fromStore.mutateAsync(url));
      else if (file?.items.length) done(await fromFile.mutateAsync(file.items));
    } catch (err) {
      const reason = err instanceof ApiError ? String(err.details?.reason ?? "") : "";
      setError(IMPORT_ERRORS[reason] ?? (err instanceof ApiError ? err.message : "No se pudo importar."));
    }
  }

  async function onFile(f: File | undefined) {
    setError(null);
    if (!f) return setFile(null);
    const result = readProductsCsv(await f.text());
    setFile(result);
    if (result.missingName)
      setError('El archivo necesita una columna "nombre". Descarga la plantilla para ver el formato.');
  }

  function downloadTemplate() {
    const blob = new Blob([`﻿${CSV_TEMPLATE}`], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "plantilla-productos-orbis.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const pending = fromStore.isPending || fromFile.isPending;
  return (
    <dialog ref={dialog} onClose={onClose} aria-labelledby="imp-title">
      <form className="dialog-form" onSubmit={onSubmit}>
        <h3 id="imp-title">Importar productos</h3>
        <fieldset className="filters">
          <legend className="sr-only">Desde dónde</legend>
          <button type="button" aria-pressed={mode === "file"} onClick={() => setMode("file")}>
            Archivo CSV
          </button>
          <button type="button" aria-pressed={mode === "store"} onClick={() => setMode("store")}>
            Tienda en línea
          </button>
        </fieldset>
        {mode === "file" ? (
          <>
            <p className="muted">
              Exporta el catálogo desde Excel, Google Sheets o tu sistema como CSV. Columnas: nombre (obligatoria),
              modelo, categoría, descripción, especificaciones ("Material: Acero; Capacidad: 12 tazas"), fotos y enlace.
              Los precios nunca se importan.
            </p>
            <div className="field">
              <label htmlFor="imp-file">Archivo</label>
              <input
                className="input"
                id="imp-file"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => onFile(e.target.files?.[0])}
              />
            </div>
            {file && !file.missingName && (
              <p className="muted">
                {file.items.length} productos listos
                {file.skipped ? `, ${file.skipped} filas sin nombre se omiten` : ""}.
              </p>
            )}
            <button className="btn btn-ghost btn-sm" type="button" onClick={downloadTemplate}>
              Descargar plantilla
            </button>
          </>
        ) : (
          <>
            <p className="muted">
              Orbis lee el catálogo público de una tienda WooCommerce: nombres, modelos, fotos y características. Si la
              tienda no lo permite, usa un archivo CSV.
            </p>
            <div className="field">
              <label htmlFor="imp-url">Dirección de la tienda</label>
              <input
                className="input"
                id="imp-url"
                placeholder="tienda.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
          </>
        )}
        <p className="muted">Si importas otra vez, se actualizan esos datos sin tocar lo que escribiste en Orbis.</p>
        {error && <div className="banner banner-warn">{error}</div>}
        <div className="row">
          <button className="btn" type="button" onClick={() => dialog.current?.close()}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={pending || (mode === "store" ? !url.trim() : !file?.items.length)}
          >
            {pending ? "Importando…" : "Importar"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

// ---- Detail / edit ---------------------------------------------------------

type Draft = Omit<Product, "id" | "externalId" | "createdAt" | "updatedAt">;

/** A draft from what the API sends: the product page's links are not part of the product. */
const toDraft = ({ id, externalId, createdAt, updatedAt, fitsProducts, parts, ...rest }: ProductDetail): Draft => rest;

const label = (p: { name: string; model: string | null }) => (p.model ? `${p.model} · ${p.name}` : p.name);

const KIND_WORDS = {
  product: { created: "Producto creado", create: "Crear producto" },
  service: { created: "Servicio creado", create: "Crear servicio" },
  part: { created: "Repuesto creado", create: "Crear repuesto" },
} as const;

const EMPTY: Draft = {
  kind: "product",
  name: "",
  model: null,
  category: null,
  description: null,
  specs: [],
  images: [],
  url: null,
  warrantyMonths: null,
  spareParts: false,
  fits: [],
  durationMinutes: null,
  modalities: [],
  price: null,
  priceBusiness: null,
  priceInstitution: null,
  currency: "CRC",
  priceUnit: null,
  showPrice: true,
  status: "active",
  internalNotes: null,
};

const MODALITY_OPTIONS = [
  { value: "local", label: "En el local" },
  { value: "home", label: "A domicilio" },
  { value: "online", label: "En línea" },
] as const;

/** A number field that keeps null for empty. */
const num = (v: string) => (v.trim() === "" ? null : Number(v));

export function ProductPage() {
  const { productId } = useParams({ from: "/app/catalogo/$productId" });
  const { para } = useSearch({ from: "/app/catalogo/$productId" });
  const isNew = productId === "nuevo";
  const product = useProduct(isNew ? undefined : productId);
  if (!isNew && product.isPending) return <div className="page">Cargando…</div>;
  if (!isNew && !product.data) return <div className="page">Ese producto ya no existe.</div>;
  return (
    <ProductForm
      key={product.data?.id ?? `nuevo:${para ?? ""}`}
      product={product.data ?? null}
      fitsFirst={isNew ? para : undefined}
    />
  );
}

function ProductForm({ product, fitsFirst }: { product: ProductDetail | null; fitsFirst?: string }) {
  const canEdit = useCanEdit();
  const categories = useProductCategories();
  const save = useSaveProduct();
  const remove = useDeleteProduct();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  const [d, setD] = useState<Draft>(() =>
    product
      ? { ...EMPTY, ...toDraft(product) }
      : fitsFirst
        ? { ...EMPTY, kind: "part", fits: [fitsFirst], category: "Repuestos" }
        : EMPTY,
  );
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((prev) => ({ ...prev, [k]: v }));
  const txt = (k: keyof Draft) => (d[k] as string | null) ?? "";
  const service = d.kind === "service";
  const part = d.kind === "part";
  const missing = missingFields(d);
  const before = product ? toDraft(product) : null;
  const dirty =
    !!before && JSON.stringify({ ...before, ...d, images: [] }) !== JSON.stringify({ ...before, images: [] });
  // What a part can fit: any product or service of the catalog, active or not (old models too).
  const catalog = useProducts("", "", { withInactive: true });
  const fitOptions = useMemo(() => (catalog.data?.items ?? []).filter((p) => p.kind !== "part"), [catalog.data]);
  const byId = useMemo(() => new Map(fitOptions.map((p) => [p.id, p])), [fitOptions]);
  const fitLabel = (id: string) => {
    const p = byId.get(id) ?? product?.fitsProducts.find((f) => f.id === id);
    return p ? label(p) : "Producto eliminado";
  };
  const policy = useLumiConfig().data?.pricePolicy;
  const hours = d.durationMinutes != null ? Math.floor(d.durationMinutes / 60) : "";
  const minutes = d.durationMinutes != null ? d.durationMinutes % 60 : "";
  const setDuration = (h: number | "", m: number | "") =>
    set("durationMinutes", h === "" && m === "" ? null : Number(h || 0) * 60 + Number(m || 0) || null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      const specs = d.specs.filter((s) => s.label.trim() && s.value.trim());
      const saved = await save.mutateAsync({ id: product?.id, input: { ...d, specs } });
      toast(product ? "Guardado" : KIND_WORDS[d.kind].created);
      if (!product) navigate({ to: "/catalogo/$productId", params: { productId: saved.id } });
    } catch (err) {
      toast(
        err instanceof ApiError && err.code === "conflict"
          ? "Otro producto ya usa ese modelo o código."
          : err instanceof ApiError
            ? err.message
            : "No se pudo guardar.",
      );
    }
  }

  async function onDelete() {
    if (!product) return;
    const ok = await confirm({
      title: `¿Eliminar ${product.name}?`,
      message: "Se borra del catálogo de Orbis y la IA deja de conocerlo. No cambia nada en tu tienda en línea.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    await remove.mutateAsync(product.id);
    toast("Producto eliminado");
    navigate({ to: "/catalogo" });
  }

  return (
    <div className="page">
      <Link className="back-link" to="/catalogo">
        <BackIcon />
        Catálogo
      </Link>
      <form className="product-form" onSubmit={onSubmit}>
        <fieldset disabled={!canEdit} className="product-grid">
          <div className="card product-col">
            <div className="card-body">
              <fieldset className="filters">
                <legend className="sr-only">Tipo</legend>
                <button type="button" aria-pressed={d.kind === "product"} onClick={() => set("kind", "product")}>
                  Producto
                </button>
                <button type="button" aria-pressed={service} onClick={() => set("kind", "service")}>
                  Servicio
                </button>
                <button type="button" aria-pressed={part} onClick={() => set("kind", "part")}>
                  Repuesto
                </button>
              </fieldset>
              <div className="field">
                <label htmlFor="pf-name">Nombre</label>
                <input
                  className="input"
                  id="pf-name"
                  placeholder={
                    service
                      ? "Ej.: Instalación de plantilla de gas"
                      : part
                        ? "Ej.: Vaso de vidrio para licuadora"
                        : "Ej.: Plantilla de gas de 2 quemadores"
                  }
                  required
                  maxLength={200}
                  value={d.name}
                  onChange={(e) => set("name", e.target.value)}
                />
              </div>
              <div className="two-cols">
                <div className="field">
                  <label htmlFor="pf-model">
                    {service ? "Código (opcional)" : part ? "Código del repuesto" : "Modelo"}
                  </label>
                  <input
                    className="input mono"
                    id="pf-model"
                    maxLength={80}
                    placeholder={service ? "Ej.: SRV-01" : part ? "Ej.: VL-3" : "Ej.: CF-12DV"}
                    value={txt("model")}
                    onChange={(e) => set("model", e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="pf-category">Categoría</label>
                  <input
                    className="input"
                    id="pf-category"
                    placeholder={service ? "Ej.: Instalaciones" : part ? "Ej.: Repuestos" : "Ej.: Plantillas"}
                    list="pf-categories"
                    maxLength={80}
                    value={txt("category")}
                    onChange={(e) => set("category", e.target.value)}
                  />
                  <datalist id="pf-categories">
                    {categories.data?.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
              </div>
              <div className="field">
                <label htmlFor="pf-description">Descripción</label>
                <textarea
                  className="textarea"
                  id="pf-description"
                  placeholder={
                    service
                      ? "Qué incluye, para quién es y qué necesita el cliente"
                      : part
                        ? "Qué pieza es y cómo se reconoce (tamaño, color, dónde va)"
                        : "Para qué sirve, para quién es y qué lo distingue"
                  }
                  rows={5}
                  maxLength={4000}
                  value={txt("description")}
                  onChange={(e) => set("description", e.target.value)}
                />
              </div>
              {canEdit && product && (
                <ImproveDescription
                  productId={product.id}
                  current={txt("description")}
                  dirty={dirty}
                  onUse={(text) => set("description", text)}
                />
              )}
              <div className="field">
                <span className="label">{service ? "Detalles" : "Especificaciones"}</span>
                <span className="faint">
                  {service
                    ? "Lo que el cliente pregunta: qué incluye, requisitos, garantía del trabajo."
                    : "Que digan en qué se diferencia de los parecidos: material, tamaño, capacidad…"}
                </span>
                <div className="spec-rows">
                  {d.specs.map((s, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows are edited in place
                    <div className="spec-row" key={i}>
                      <input
                        className="input"
                        aria-label="Característica"
                        placeholder={service ? "Incluye" : "Material"}
                        value={s.label}
                        maxLength={80}
                        onChange={(e) =>
                          set(
                            "specs",
                            d.specs.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                          )
                        }
                      />
                      <input
                        className="input"
                        aria-label="Valor"
                        placeholder={service ? "Revisión de fugas" : "Acero inoxidable"}
                        value={s.value}
                        maxLength={300}
                        onChange={(e) =>
                          set(
                            "specs",
                            d.specs.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)),
                          )
                        }
                      />
                      <button
                        className="btn btn-sm"
                        type="button"
                        aria-label="Quitar"
                        onClick={() =>
                          set(
                            "specs",
                            d.specs.filter((_, j) => j !== i),
                          )
                        }
                      >
                        Quitar
                      </button>
                    </div>
                  ))}
                  <button
                    className="btn btn-sm"
                    type="button"
                    onClick={() => set("specs", [...d.specs, { label: "", value: "" }])}
                  >
                    {service ? "Agregar detalle" : "Agregar especificación"}
                  </button>
                </div>
              </div>
              <PhotoField
                productId={product?.id ?? null}
                images={d.images}
                canEdit={canEdit}
                onChange={(images) => set("images", images)}
              />
              <p className={`product-complete${missing.length ? "" : " done"}`}>
                {missing.length
                  ? `Para que Lumi lo explique bien, falta: ${missing.join(", ")}.`
                  : "Ficha completa: Lumi tiene lo que necesita para explicarlo."}
              </p>
            </div>
          </div>

          <div className="product-col">
            <div className="card">
              <div className="card-body">
                <h3>Estado</h3>
                <div className="field">
                  <label htmlFor="pf-status">¿Lumi lo ofrece?</label>
                  <select
                    className="select"
                    id="pf-status"
                    value={d.status}
                    onChange={(e) => set("status", e.target.value as Draft["status"])}
                  >
                    <option value="active">Activo: Lumi lo ofrece</option>
                    <option value="inactive">Inactivo: ya no se ofrece</option>
                  </select>
                  <span className="faint">
                    Inactivo: Lumi no lo ofrece ni lo recomienda, pero puede ayudar con su garantía o su manual. Se
                    oculta de la lista.
                  </span>
                </div>
              </div>
            </div>
            {part && (
              <div className="card">
                <div className="card-body">
                  <h3>Sirve para</h3>
                  <p className="muted">
                    Los productos a los que les sirve. Lumi solo lo ofrece para estos modelos; si el cliente tiene otro,
                    lo pasa con un asesor.
                  </p>
                  {d.fits.length > 0 && (
                    <ul className="kb-products">
                      {d.fits.map((id) => (
                        <li key={id}>
                          <span className="kb-product-name" title={fitLabel(id)}>
                            {fitLabel(id)}
                          </span>
                          {canEdit && (
                            <button
                              className="btn btn-sm"
                              type="button"
                              aria-label={`Quitar ${fitLabel(id)}`}
                              onClick={() =>
                                set(
                                  "fits",
                                  d.fits.filter((x) => x !== id),
                                )
                              }
                            >
                              Quitar
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {canEdit && (
                    <select
                      className="select"
                      aria-label="Agregar producto"
                      value=""
                      onChange={(e) => e.target.value && set("fits", [...d.fits, e.target.value])}
                    >
                      <option value="">Agregar producto…</option>
                      {fitOptions
                        .filter((p) => !d.fits.includes(p.id))
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {label(p)}
                          </option>
                        ))}
                    </select>
                  )}
                </div>
              </div>
            )}
            {service && (
              <div className="card">
                <div className="card-body">
                  <h3>Servicio</h3>
                  <div className="field">
                    <span className="label">Duración</span>
                    <div className="two-cols">
                      <input
                        className="input"
                        type="number"
                        min={0}
                        max={168}
                        aria-label="Horas"
                        placeholder="Horas, ej.: 1"
                        value={hours}
                        onChange={(e) => setDuration(e.target.value === "" ? "" : Number(e.target.value), minutes)}
                      />
                      <input
                        className="input"
                        type="number"
                        min={0}
                        max={59}
                        aria-label="Minutos"
                        placeholder="Minutos, ej.: 30"
                        value={minutes}
                        onChange={(e) => setDuration(hours, e.target.value === "" ? "" : Number(e.target.value))}
                      />
                    </div>
                  </div>
                  <fieldset className="choice">
                    <legend>Dónde se da</legend>
                    <div className="lumi-checks">
                      {MODALITY_OPTIONS.map((m) => (
                        <label key={m.value} className="check">
                          <input
                            type="checkbox"
                            checked={d.modalities.includes(m.value)}
                            onChange={() =>
                              set(
                                "modalities",
                                d.modalities.includes(m.value)
                                  ? d.modalities.filter((x) => x !== m.value)
                                  : [...d.modalities, m.value],
                              )
                            }
                          />
                          {m.label}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </div>
              </div>
            )}
            <div className="card">
              <div className="card-body">
                <h3>Precio (opcional)</h3>
                {policy === "no_share" && (
                  <p className="faint">
                    Ahora Lumi no da precios: los pasa a un asesor. Se cambia en{" "}
                    <Link to="/lumi" search={{ tab: "precios" }}>
                      Lumi → Precios
                    </Link>
                    .
                  </p>
                )}
                <div className="two-cols">
                  <div className="field">
                    <label htmlFor="pf-price">{policy === "by_customer_type" ? "Para la casa" : "Precio"}</label>
                    <input
                      className="input"
                      id="pf-price"
                      type="number"
                      min={0}
                      step="any"
                      placeholder="Ej.: 25000"
                      value={d.price ?? ""}
                      onChange={(e) => set("price", num(e.target.value))}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="pf-currency">Moneda</label>
                    <select
                      className="select"
                      id="pf-currency"
                      value={d.currency}
                      onChange={(e) => set("currency", e.target.value as Draft["currency"])}
                    >
                      <option value="CRC">Colones (₡)</option>
                      <option value="USD">Dólares (US$)</option>
                    </select>
                  </div>
                </div>
                {policy === "by_customer_type" && (
                  <div className="two-cols">
                    <div className="field">
                      <label htmlFor="pf-price-business">Para negocios</label>
                      <input
                        className="input"
                        id="pf-price-business"
                        type="number"
                        min={0}
                        step="any"
                        placeholder="Vacío: el mismo"
                        value={d.priceBusiness ?? ""}
                        onChange={(e) => set("priceBusiness", num(e.target.value))}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="pf-price-institution">Para instituciones</label>
                      <input
                        className="input"
                        id="pf-price-institution"
                        type="number"
                        min={0}
                        step="any"
                        placeholder="Vacío: el mismo"
                        value={d.priceInstitution ?? ""}
                        onChange={(e) => set("priceInstitution", num(e.target.value))}
                      />
                    </div>
                  </div>
                )}
                <div className="field">
                  <label htmlFor="pf-price-unit">Detalle del precio (opcional)</label>
                  <input
                    className="input"
                    id="pf-price-unit"
                    maxLength={40}
                    placeholder={service ? "Ej.: por visita, por hora, desde" : "Ej.: por unidad, desde"}
                    value={txt("priceUnit")}
                    onChange={(e) => set("priceUnit", e.target.value)}
                  />
                </div>
                <label className="check">
                  <input type="checkbox" checked={d.showPrice} onChange={(e) => set("showPrice", e.target.checked)} />
                  Lumi puede decir este precio
                </label>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <h3>{d.kind === "product" ? "Postventa" : "Más datos"}</h3>
                <div className="two-cols">
                  <div className="field">
                    <label htmlFor="pf-warranty">Garantía (meses)</label>
                    <input
                      className="input"
                      id="pf-warranty"
                      placeholder="Ej.: 12"
                      type="number"
                      min={0}
                      max={240}
                      value={d.warrantyMonths ?? ""}
                      onChange={(e) => set("warrantyMonths", e.target.value === "" ? null : Number(e.target.value))}
                    />
                  </div>
                </div>
                {d.kind === "product" && (
                  <div className="field">
                    <span className="label">Repuestos y accesorios</span>
                    {product?.parts.length ? (
                      <ul className="kb-products">
                        {product.parts.map((r) => (
                          <li key={r.id}>
                            <Link className="kb-product-name" to="/catalogo/$productId" params={{ productId: r.id }}>
                              {label(r)}
                            </Link>
                            {r.status === "inactive" && <span className="pill pill-closed">Inactivo</span>}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <label className="check">
                        <input
                          type="checkbox"
                          checked={d.spareParts}
                          onChange={(e) => set("spareParts", e.target.checked)}
                        />
                        Tenemos repuestos de este producto
                      </label>
                    )}
                    {canEdit && product && (
                      <Link
                        className="btn btn-sm product-parts-new"
                        to="/catalogo/$productId"
                        params={{ productId: "nuevo" }}
                        search={{ para: product.id }}
                      >
                        <PlusIcon />
                        Nuevo repuesto para este producto
                      </Link>
                    )}
                    <span className="faint">
                      Cargados como repuestos, Lumi los encuentra cuando el cliente pide una pieza para este modelo.
                    </span>
                  </div>
                )}
                <div className="field">
                  <label htmlFor="pf-url">Página en la web</label>
                  <input
                    className="input"
                    id="pf-url"
                    type="url"
                    placeholder="https://…"
                    value={txt("url")}
                    onChange={(e) => set("url", e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <h3>Notas internas</h3>
                <p className="muted">
                  La IA nunca ve estas notas. Sirven para el equipo: condiciones, proveedor, precios.
                </p>
                <textarea
                  className="textarea"
                  aria-label="Notas internas"
                  placeholder="Ej.: Proveedor, condiciones especiales"
                  rows={4}
                  maxLength={4000}
                  value={txt("internalNotes")}
                  onChange={(e) => set("internalNotes", e.target.value)}
                />
              </div>
            </div>
          </div>
        </fieldset>
        {canEdit && (
          <div className="product-save">
            {product && (
              <button className="btn btn-danger" type="button" onClick={onDelete}>
                Eliminar
              </button>
            )}
            <button className="btn btn-primary" type="submit" disabled={save.isPending || !d.name.trim()}>
              {save.isPending ? "Guardando…" : product ? "Guardar cambios" : KIND_WORDS[d.kind].create}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
